import { ref, onMounted, onUnmounted } from "vue"
import {
  createMessageSender,
  parseVitableConnectMessage,
  VitableConnectMessageType,
  isAllowedOrigin,
  type MessageOf,
  type VitableConnectMessage
} from "@vitable/connect/core"
import { useEmbedMode } from "./useEmbedMode"
import { setCapturedOrigin, __getCapturedOrigin } from "../../services/embed-service"

export interface WidgetMessageCallbacks {
  onInit?: (payload: MessageOf<"VITABLE_CONNECT_INIT">["payload"]) => void
  onTokenUpdate?: (payload: MessageOf<"TOKEN_UPDATE">["payload"]) => void
  onThemeUpdate?: (payload: MessageOf<"THEME_UPDATE">["payload"]) => void
}

// --- Global singleton message listener + callback registry ---

let globalListenerRegistered = false
const callbackRegistry = new Map<string, WidgetMessageCallbacks>()
const lastReceivedMessages = new Map<VitableConnectMessageType, VitableConnectMessage>()

function dispatchToCallbacks(message: VitableConnectMessage, callbacks: WidgetMessageCallbacks): void {
  switch (message.type) {
    case VitableConnectMessageType.VITABLE_CONNECT_INIT:
      callbacks.onInit?.(message.payload)
      break
    case VitableConnectMessageType.TOKEN_UPDATE:
      callbacks.onTokenUpdate?.(message.payload)
      break
    case VitableConnectMessageType.THEME_UPDATE:
      callbacks.onThemeUpdate?.(message.payload)
      break
  }
}

function globalMessageHandler(event: MessageEvent): void {
  const message = parseVitableConnectMessage(event.data)
  if (!message) return

  const captured = __getCapturedOrigin()

  if (message.type === VitableConnectMessageType.VITABLE_CONNECT_INIT) {
    // Accept INIT only when no origin captured yet (bootstrap) or from the same origin
    if (captured && event.origin !== captured) {
      return
    }
    if (!captured) {
      setCapturedOrigin(event.origin)
    }
  } else if (!captured || event.origin !== captured) {
    return
  }

  lastReceivedMessages.set(message.type, message)
  for (const cb of callbackRegistry.values()) {
    dispatchToCallbacks(message, cb)
  }
}

export function registerMessageCallbacks(key: string, callbacks: WidgetMessageCallbacks): void {
  callbackRegistry.set(key, callbacks)
  if (!globalListenerRegistered) {
    window.addEventListener("message", globalMessageHandler)
    globalListenerRegistered = true
  }
  // Replay last received messages to late subscribers
  for (const msg of lastReceivedMessages.values()) {
    dispatchToCallbacks(msg, callbacks)
  }
}

export function unregisterMessageCallbacks(key: string): void {
  callbackRegistry.delete(key)
}

export function __resetCallbackRegistry(): void {
  callbackRegistry.clear()
  lastReceivedMessages.clear()
  if (globalListenerRegistered) {
    window.removeEventListener("message", globalMessageHandler)
    globalListenerRegistered = false
  }
}

// --- End global listener ---

/**
 * Per-composable messaging — unchanged from original implementation.
 * Existing 5 consumers use only { send, isReady } and are unaffected.
 *
 * @param callbacks - **@deprecated** Use {@link registerMessageCallbacks} instead.
 *   Passing callbacks here creates a second `window.addEventListener("message", ...)`
 *   that may dispatch duplicate events alongside the global handler.
 */
export function useEmbedMessaging(callbacks?: WidgetMessageCallbacks) {
  const { isEmbedded, parentOrigin } = useEmbedMode()
  const isReady = ref(false)

  // TODO: The wildcard fallback ("*") means messages broadcast to all windows
  // if parentOrigin is null (e.g. document.referrer blocked). Audit (2026-03):
  // all 5 consumers send only ERROR and ENROLLMENT_COMPLETE — no tokens or PII.
  // Auth messages use sendToParent() from embed-service.ts which correctly drops
  // without a captured origin. Changing this to drop would be safer but risks
  // breaking existing integrations during the brief pre-INIT window.
  const send = createMessageSender(
    (message: unknown, origin: string) => window.parent.postMessage(message, origin),
    () => parentOrigin.value ?? "*"
  )

  function handleMessage(event: MessageEvent): void {
    const allowedOrigin = parentOrigin.value

    if (!allowedOrigin || !isAllowedOrigin(event.origin, [allowedOrigin])) {
      return
    }

    const message = parseVitableConnectMessage(event.data)
    if (!message) return

    switch (message.type) {
      case VitableConnectMessageType.VITABLE_CONNECT_INIT:
        isReady.value = true
        callbacks?.onInit?.(message.payload)
        break
      case VitableConnectMessageType.TOKEN_UPDATE:
        callbacks?.onTokenUpdate?.(message.payload)
        break
    }
  }

  onMounted(() => {
    if (isEmbedded.value) {
      window.addEventListener("message", handleMessage)
    }
  })

  onUnmounted(() => {
    window.removeEventListener("message", handleMessage)
  })

  return { send, isReady }
}
